import * as React from 'react';

import { Button, CircularProgress, Dialog, IconButton } from '@mui/material';
import { Download, X } from 'lucide-react';

import { fetchAttachmentDownloadUrl } from '@modules/_shared/components/chat/chatAttachmentOriginal';
import { useLocale } from '@shared/hooks/locale';

type Props = {
  src: string | null;
  alt?: string;
  onClose: () => void;
  // Download is offered only for a persisted chat attachment.
  chatId?: string | null;
  attachmentId?: string;
  filename?: string;
  // E.g. an expired signed URL: the caller can sign a new one.
  onError?: () => void;
};

const overlayButtonSx = {
  color: '#fff',
  backgroundColor: 'rgba(255, 255, 255, 0.15)',
  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.3)' },
} as const;

const triggerDownload = (url: string, filename?: string) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename ?? '';
  link.rel = 'noreferrer';
  document.body.appendChild(link);
  link.click();
  link.remove();
};

const DownloadOriginalButton = ({
  chatId,
  attachmentId,
  filename,
}: {
  chatId: string;
  attachmentId: string;
  filename?: string;
}) => {
  const { t } = useLocale();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const download = async () => {
    setLoading(true);
    setError(null);
    try {
      triggerDownload(
        await fetchAttachmentDownloadUrl({ chatId, attachmentId, filename }),
        filename,
      );
    } catch (downloadError) {
      setError(
        downloadError instanceof Error
          ? downloadError.message
          : String(downloadError),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      onClick={e => e.stopPropagation()}
      css={{
        position: 'fixed',
        top: 12,
        right: 64,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 8,
        maxWidth: 'min(420px, calc(100vw - 88px))',
      }}
    >
      <Button
        size="small"
        disabled={loading}
        onClick={() => void download()}
        startIcon={
          loading ? (
            <CircularProgress size={14} color="inherit" />
          ) : (
            <Download size={16} />
          )
        }
        sx={{
          ...overlayButtonSx,
          height: 40,
          px: 1.5,
          '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.6)' },
        }}
      >
        {t('Download')}
      </Button>
      {error != null && (
        <pre
          role="alert"
          css={{
            margin: 0,
            padding: '8px 10px',
            borderRadius: 6,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: '#ffb4ab',
            fontSize: '0.75rem',
            lineHeight: 1.5,
            fontFamily: "'Fira Code', 'Consolas', 'Monaco', monospace",
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            maxHeight: 220,
            overflow: 'auto',
          }}
        >
          {error}
        </pre>
      )}
    </div>
  );
};

// Full-screen lightbox for chat image attachments. Clicking anywhere outside
// the image (or the close button, or Escape) dismisses it.
export const ChatImagePreviewDialog = ({
  src,
  alt,
  onClose,
  chatId,
  attachmentId,
  filename,
  onError,
}: Props) => {
  const { t } = useLocale();
  return (
    <Dialog
      open={src != null}
      onClose={onClose}
      fullScreen
      aria-label={t('Preview')}
      slotProps={{
        paper: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.88)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
          onClick: onClose,
        },
      }}
    >
      <IconButton
        onClick={onClose}
        title={t('Close')}
        sx={{
          position: 'fixed',
          top: 12,
          right: 12,
          ...overlayButtonSx,
        }}
      >
        <X size={22} />
      </IconButton>
      {src != null && chatId != null && attachmentId != null && (
        <DownloadOriginalButton
          chatId={chatId}
          attachmentId={attachmentId}
          filename={filename}
        />
      )}
      {src != null && (
        <img
          src={src}
          alt={alt ?? t('Preview')}
          onClick={e => e.stopPropagation()}
          onError={onError}
          css={{
            maxWidth: 'calc(100vw - 32px)',
            maxHeight: 'calc(100vh - 32px)',
            objectFit: 'contain',
            borderRadius: 4,
          }}
        />
      )}
    </Dialog>
  );
};
